import { useMutation } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useCallback, useRef } from 'react';

import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { useToaster } from '@/hooks/useToaster';
import type { ConversationError } from '@/services/conversations/errors';
import { resolveSupportThread } from '@/services/conversations/thread';
import { trackSupportThreadStarted } from '@/util/analyticsEvents';

const GENERIC_FAILURE = 'Please try again.';

/** The refusals staff can meet when picking a person, in words that say what to do instead. */
const REFUSAL_MESSAGES: Record<string, string> = {
  SUPPORT_SELF: 'That is the Primum Support account. Choose a member to message.',
  RECIPIENT_ROUTING_ACCOUNT: 'That account routes inquiries and can’t be messaged.',
  RECIPIENT_CLOSED: 'That account is closed and can’t be messaged.',
  RECIPIENT_NOT_FOUND: 'That person could not be found.',
};

/**
 * A 403 means the viewer may not start support threads, which in practice is
 * staff who opened New message without acting as Primum Support. Retrying
 * cannot help, so it says what will.
 */
const FORBIDDEN_FAILURE = 'Act as Primum Support to message members.';

export function openSupportThreadFailureMessage(error: ConversationError): string {
  if (error.code && REFUSAL_MESSAGES[error.code]) {
    return REFUSAL_MESSAGES[error.code];
  }

  return error.kind === 'forbidden' ? FORBIDDEN_FAILURE : GENERIC_FAILURE;
}

/**
 * Opens a member's support thread from the new-message screen: resolves it,
 * creating it empty if needed, then replaces the picker with the thread so back
 * returns to the inbox. On a compact layout the thread opens on its own route;
 * on desktop the inbox opens with it selected.
 *
 * One resolution runs at a time, so a double tap opens the thread once. A
 * refusal leaves staff on the picker with a message saying why.
 */
export function useOpenSupportThread() {
  const isCompactLayout = useIsCompactLayout();

  const toast = useToaster();

  /** Set synchronously on the first tap, so a second tap in the same frame is ignored too. */
  const isResolvingRef = useRef(false);

  const mutation = useMutation<
    Awaited<ReturnType<typeof resolveSupportThread>>,
    ConversationError,
    number
  >({
    mutationFn: resolveSupportThread,
    onSuccess: ({ conversationId, created }, memberId) => {
      trackSupportThreadStarted({ conversation_id: conversationId, member_id: memberId, created });

      if (isCompactLayout) {
        router.replace({ pathname: '/inbox/chat/[id]', params: { id: conversationId } });

        return;
      }

      router.replace({ pathname: '/inbox', params: { id: conversationId } });
    },
    onError: (error) =>
      toast.showError({
        title: 'Couldn’t open the conversation',
        message: openSupportThreadFailureMessage(error),
      }),
    onSettled: () => {
      isResolvingRef.current = false;
    },
  });

  const { mutate, isPending, variables } = mutation;

  const open = useCallback(
    (memberId: number) => {
      if (isResolvingRef.current) {
        return;
      }

      isResolvingRef.current = true;

      mutate(memberId);
    },
    [mutate]
  );

  return { open, pendingMemberId: isPending ? variables : undefined };
}
